// Setup › Technicians' iPads on the SettingsPage pattern (BL-189): every iPad paired here (devices.list),
// for the owner and managers, then how the iPad behaves on the job and what it keeps (RULE_SECTIONS). As
// Devices.tsx's DevicesTab draws them in Blueprint, with nothing changed: each iPad says who it's paired
// to, its app (and whether it should be updated), when it last synced, what it hasn't sent and since
// when, and what it sent after it was signed out, waiting in Review. "Sign out…" signs one iPad out with
// a reason (devices.revoke) in a ConfirmDialog: it wipes itself the next time it connects. What a person
// left sealed on an iPad (QA-177) the owner alone discards for good (devices.discardSealed, BL-073).
// Riverstone keeps DevicesTab.
import { useCallback, useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { ConfirmDialog, Link } from '@flowology/ui-web/fl';
import { SettingRow, SettingsPage, SettingsSection } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import {
  platformWord,
  quarantinedWords,
  sealedWords,
  statusWords,
  syncedWords,
  unsentWords,
  type DeviceRow,
} from './devices-model.ts';
import { BP_LEDE, LEDE } from './ledes.ts';
import { settingsPage } from './parts.ts';
import { settingsRights } from './settings-model.ts';
import { SetupRuleSections } from './SetupRules.tsx';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

export function DevicesPage({ nav, timeZone }: { nav: NavProps; timeZone: string }) {
  const held = nav.permissions ?? [];
  const rights = settingsRights(held);
  const crumb = usePageCrumb(nav);
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <SettingsPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: settingsPage('devices').label ?? 'Technicians’ iPads',
            lede: BP_LEDE.devices ?? LEDE.devices,
          }}
        >
          <SetupRuleSections page="devices" top rights={rights} />
          {rights.edit && (
            <Devices
              timeZone={timeZone}
              owner={held.includes('sync.sealed.discard')}
              onReview={() => nav.onView('review')}
            />
          )}
          <SetupRuleSections page="devices" rights={rights} />
        </SettingsPage>
      </main>
    </div>
  );
}

function Devices({
  timeZone,
  onReview,
  owner,
}: {
  timeZone: string;
  onReview: () => void;
  /** Only the owner discards what a person left sealed on an iPad. */
  owner: boolean;
}) {
  const [devices, setDevices] = useState<DeviceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<DeviceRow | null>(null);
  const [discarding, setDiscarding] = useState<DeviceRow | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setDevices((await client.call<{ devices: DeviceRow[] }>('devices.list', {})).devices);
      setError(null);
    } catch (e) {
      setError(message(e));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  // what was just done, said under the section's title for five seconds
  useEffect(() => {
    if (!said) return;
    const t = setTimeout(() => setSaid(null), 5000);
    return () => clearTimeout(t);
  }, [said]);
  const done = async (words: string) => {
    setSaid(words);
    await load();
  };
  const now = new Date();
  const live = devices?.filter((d) => !d.revokedAt) ?? [];
  const out = devices?.filter((d) => d.revokedAt) ?? [];
  const row = (d: DeviceRow) => {
    const status = statusWords(d, timeZone);
    const held = quarantinedWords(d.quarantined);
    const kept = sealedWords(d, timeZone);
    const warning = [d.outOfDate && 'Out of date: update the app', d.stale && 'Not synced for 12 hours']
      .filter(Boolean)
      .join(' · ');
    return (
      <SettingRow
        key={d.id}
        name={d.personName ?? 'Someone no longer here'}
        {...(warning ? { warning } : {})}
        help={
          <>
            {d.name} · {platformWord(d.platform)} · app {d.appVersion}
            <br />
            {syncedWords(d, now, timeZone)}
            {/* kept for its person rather than waiting to send: said once, below */}
            {!kept && (
              <>
                <br />
                {unsentWords(d, now, timeZone)}
              </>
            )}
            {held && (
              <>
                <br />
                {held} <Link onPress={onReview}>Open Review</Link>
              </>
            )}
            <br />
            {status.head}
            {status.wipe && (
              <>
                <br />
                {status.wipe}
              </>
            )}
            {kept && (
              <>
                <br />
                <span data-testid={`kept-${d.id}`}>
                  {kept.head} {kept.open}
                  {owner && d.staffId && (
                    <>
                      {' '}
                      <Link onPress={() => setDiscarding(d)}>Discard them…</Link>
                    </>
                  )}
                </span>
              </>
            )}
          </>
        }
        control={d.revokedAt ? null : <Link onPress={() => setRevoking(d)}>Sign out…</Link>}
      />
    );
  };
  return (
    <>
      <SettingsSection
        title="Signed in"
        {...(said ? { note: <span role="status">{said}</span> } : {})}
        status={devices ? (live.length ? 'ready' : 'empty') : error ? 'failed' : 'loading'}
        emptyText={
          devices?.length
            ? 'No iPad is signed in right now.'
            : 'No iPads are paired yet. A technician pairs one by signing in on it.'
        }
        {...(error ? { failedText: error } : {})}
        onRetry={() => void load()}
      >
        {live.map(row)}
      </SettingsSection>
      {out.length > 0 && (
        <SettingsSection
          title="Signed out"
          fold={{ summary: out.length === 1 ? '1 iPad' : `${out.length} iPads` }}
        >
          {out.map(row)}
        </SettingsSection>
      )}
      {/* over the page: a section's body is its empty or failed line until it has rows */}
      {revoking && (
        <RevokeDialog device={revoking} onClose={() => setRevoking(null)} onDone={(t) => done(t)} />
      )}
      {discarding && (
        <DiscardKeptDialog device={discarding} onClose={() => setDiscarding(null)} onDone={(t) => done(t)} />
      )}
    </>
  );
}

/** Sign one iPad out, with a reason: the server's words for what happens, and its refusal if any. */
function RevokeDialog({
  device,
  onClose,
  onDone,
}: {
  device: DeviceRow;
  onClose: () => void;
  onDone: (said: string) => Promise<void>;
}) {
  const whose = device.personName ? `${device.personName}’s ${device.name}` : device.name;
  return (
    <ConfirmDialog
      isOpen
      onClose={onClose}
      title={`Sign out ${whose}`}
      confirmLabel="Sign out this iPad"
      cancelLabel="Cancel"
      reason={{
        label: 'Why (lost, swapped, someone left)',
        missing: 'Say why it’s signed out.',
        maxLength: 300,
      }}
      onConfirm={async (why) => {
        // a refusal (an RpcError) keeps the dialog open with the server's words
        await client.call('devices.revoke', { deviceId: device.id, reason: why });
        await onDone(`${whose} is signed out. It wipes itself the next time it connects.`);
      }}
    >
      <p>
        Signs out this iPad now. Anything it hasn’t sent comes to you to review, and it wipes itself the next
        time it connects. {device.personName ? `${device.personName}’s` : 'Their'} sign-in elsewhere stays as
        it is.
      </p>
      {device.pendingCount > 0 && (
        <p>
          It still has {device.pendingCount === 1 ? '1 change' : `${device.pendingCount} changes`} it hasn’t
          sent. They come to Review once it connects.
        </p>
      )}
    </ConfirmDialog>
  );
}

/**
 * The owner discards what a person left sealed on their iPads (QA-177): never sent, by anyone, and
 * it can't be undone. With a reason, as the server asks.
 */
function DiscardKeptDialog({
  device,
  onClose,
  onDone,
}: {
  device: DeviceRow;
  onClose: () => void;
  onDone: (said: string) => Promise<void>;
}) {
  const who = device.personName ?? 'this person';
  const n = device.sealed?.count ?? 0;
  const changes = n === 1 ? 'the 1 change' : `the ${n} changes`;
  return (
    <ConfirmDialog
      isOpen
      onClose={onClose}
      title={`Discard ${who}’s kept changes`}
      confirmLabel="Discard them"
      cancelLabel="Cancel"
      reason={{ label: 'Why', missing: 'Say why they’re discarded.', maxLength: 300 }}
      onConfirm={async (why) => {
        await client.call('devices.discardSealed', { staffId: device.staffId, reason: why });
        await onDone(
          `${who}’s kept changes are discarded. The iPad drops them the next time anyone signs in on it.`,
        );
      }}
    >
      <p>
        {changes.charAt(0).toUpperCase() + changes.slice(1)} kept on {device.name} will never be sent, to you
        or to anyone, and this can’t be undone. Anything {who} left kept on another iPad here is discarded
        too.
      </p>
      <p>For when {who} won’t sign in on it again: they left, or the iPad goes to someone else.</p>
    </ConfirmDialog>
  );
}
